import { z } from 'zod';

/**
 * Single error envelope for every API route.
 *
 * The message is safe to show to a player. Anything diagnostic goes to server
 * logs keyed by `traceId`, never into the response body.
 */
export const API_ERROR_CODES = [
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'age_verification_required',
  'feature_disabled',
  /**
   * The game is between release stages and a reset is being prepared or run.
   *
   * Distinct from `feature_disabled`, which is about one capability, and from
   * a bare 503, which reads as "the server fell over". This one has a screen:
   * the client shows the maintenance notice rather than a retry.
   */
  'maintenance',
  /**
   * The deployment and the database disagree about which release stage this is.
   *
   * Not `maintenance`, which is a state somebody chose and will leave: nobody
   * chose this one, and no player action can end it. Its own code so the client
   * can say "we are fixing a configuration problem" rather than promising a
   * transition that is not happening.
   */
  'stage_mismatch',
  'internal_error',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(API_ERROR_CODES),
    message: z.string().min(1),
    /** Correlates the player-facing failure with server logs. */
    traceId: z.string().min(1).optional(),
    /**
     * How long the client should wait before repeating the SAME request,
     * computed by the server. Used by "not due yet" answers, where retrying
     * with the same sequence and key is the correct behaviour rather than an
     * error to surface.
     */
    retryAfterMs: z.number().int().min(0).optional(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
